import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Target as TargetIcon, X } from "@phosphor-icons/react";
import { Button, EmptyState, ExpandedRow, Input, PageHeader, Select, Table, TBody, TD, TDNum, TH, THead, TR } from "@ghezaa/ui";
import type { TargetMeasure, TargetRow } from "@/api/client";
import { useCopyTargets, useSetTargets, type TargetInput } from "@/api/mutations";
import { useTargets } from "@/api/queries";
import { inCurrency, monthName, percent } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";
import { figure, isMoney, measures, measureWord } from "@/lib/targets";

export type TargetsSearch = { month?: string; open?: string };

export function targetsSearch(search: Record<string, unknown>): TargetsSearch {
  return {
    ...(typeof search.month === "string" && /^\d{4}-\d{2}$/.test(search.month) ? { month: search.month } : {}),
    ...(typeof search.open === "string" && search.open !== "" ? { open: search.open } : {}),
  };
}

/** Two months ahead and twelve back, newest first. */
function monthsAround(): string[] {
  const now = new Date();
  return Array.from({ length: 15 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + 2 - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}

/**
 * The month's targets per salesperson code: at most five measures, weights adding to 100, each row set from what the
 * code did in an average month of the three before. A month gone is a record and is shown, not changed.
 */
export function TargetsPage() {
  const market = useCurrentMarket();
  const search = useSearch({ strict: false }) as TargetsSearch;
  const navigate = useNavigate();
  const targets = useTargets(market.code, search.month);
  const copy = useCopyTargets(market.code);
  const d = targets.data;
  const canEdit = Boolean(d?.editable && market.can.manage);
  const setSearch = (next: TargetsSearch) => void navigate({ to: ".", search: next as never, replace: true });
  const set = d?.rows.filter((r) => r.lines.length > 0).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Targets"
        description={d ? `${monthName(d.month)}: ${set} of ${d.rows.length} salespeople have targets.` : undefined}
        actions={
          canEdit &&
          d?.previousSet && (
            <Button onClick={() => copy.mutate(d.month)} disabled={copy.isPending}>
              Copy from {monthName(d.previous)}
            </Button>
          )
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <Select aria-label="Month" value={search.month ?? d?.month ?? ""} onChange={(e) => setSearch({ month: e.target.value })} className="w-auto">
          {monthsAround().map((m) => (
            <option key={m} value={m}>
              {monthName(m)}
            </option>
          ))}
        </Select>
        {copy.data && <span className="text-sm text-ink-3">{copy.data.copied === 0 ? "Every salesperson had targets already." : `Copied for ${copy.data.copied}.`}</span>}
      </div>
      {targets.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the targets: {targets.error.message}</p>}
      {copy.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">{copy.error.message}</p>}
      {!d && !targets.isError && <div className="h-40 bg-surface-2" aria-busy="true" />}
      {d && d.rows.length === 0 && <EmptyState icon={TargetIcon} title="No salesperson codes yet" description="They come from Business Central: link the market to its company first." />}
      {d && d.rows.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>Salesperson</TH>
              <TH>Targets</TH>
              <TH from="md" className="text-end whitespace-nowrap">Of target</TH>
              <TH from="lg" className="text-end whitespace-nowrap">{inCurrency("Usual revenue", market.currency)}</TH>
            </TR>
          </THead>
          <TBody>
            {d.rows.map((r) => {
              const selected = search.open === r.code;
              return [
                <TR key={r.code} data-state={selected ? "selected" : undefined}>
                  <TD className="w-full max-w-0">
                    {canEdit ? (
                      <button type="button" onClick={() => setSearch({ month: d.month, open: selected ? undefined : r.code })} aria-expanded={selected} className="block truncate text-start underline-offset-2 hover:underline">
                        {r.name || r.code}
                      </button>
                    ) : (
                      <span className="block truncate">{r.name || r.code}</span>
                    )}
                    <span className="block truncate text-xs text-ink-3">{[r.person ?? "Not linked to anyone", r.code].join(" · ")}</span>
                  </TD>
                  <TD className="whitespace-nowrap text-sm">
                    {r.lines.length === 0 ? (
                      <span className="text-ink-3">None set</span>
                    ) : (
                      <span className="flex flex-col">
                        {r.lines.map((l) => (
                          <span key={l.measure} className="whitespace-nowrap">
                            {measureWord[l.measure]} {figure(l.measure, l.target, true)}
                            <span className="text-ink-3"> · {l.weight}%</span>
                          </span>
                        ))}
                      </span>
                    )}
                  </TD>
                  <TDNum from="md">{r.overall === null ? "" : percent(r.overall)}</TDNum>
                  <TDNum from="lg">{r.usual.Revenue ? figure("Revenue", r.usual.Revenue) : ""}</TDNum>
                </TR>,
                selected && canEdit && (
                  <ExpandedRow key={`${r.code}-open`} colSpan={4}>
                    <TargetEditor row={r} month={d.month} currency={market.currency} onDone={() => setSearch({ month: d.month })} />
                  </ExpandedRow>
                ),
              ];
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}

type Draft = { measure: TargetMeasure; value: string; weight: string };

/** What a person types for a measure's target: money and counts as they are, a margin in percent. */
function typed(measure: TargetMeasure, value: number): string {
  return measure === "Margin" ? String(Math.round(value * 1000) / 10) : String(value);
}

function parsed(measure: TargetMeasure, value: string): number {
  const n = Number(value.replace(/,/g, ""));
  return measure === "Margin" ? n / 100 : n;
}

/** The open row: up to five measures with their target and weight, each beside the code's usual month. */
function TargetEditor({ row, month, currency, onDone }: { row: TargetRow; month: string; currency: string; onDone: () => void }) {
  const market = useCurrentMarket();
  const save = useSetTargets(market.code);
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    row.lines.length > 0
      ? row.lines.map((l) => ({ measure: l.measure, value: typed(l.measure, l.target), weight: String(l.weight) }))
      : [{ measure: "Revenue", value: row.usual.Revenue ? String(row.usual.Revenue) : "", weight: "100" }],
  );

  useEffect(() => {
    const close = (e: KeyboardEvent) => e.key === "Escape" && onDone();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onDone]);

  const used = new Set(drafts.map((d) => d.measure));
  const weights = drafts.reduce((sum, d) => sum + (Number(d.weight) || 0), 0);
  const change = (i: number, patch: Partial<Draft>) => setDrafts(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const errors = (save.error as { errors?: Record<string, string[]> } | null)?.errors ?? {};
  const submit = (targets: TargetInput[]) => save.mutate({ month, code: row.code, targets }, { onSuccess: onDone });

  return (
    <form
      className="flex max-w-[720px] flex-col gap-4 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit(drafts.map((d) => ({ measure: d.measure, value: parsed(d.measure, d.value), weight: Number(d.weight) })));
      }}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_120px_72px_32px] items-end gap-x-3 gap-y-2">
        <span className="text-2xs font-medium uppercase tracking-wider text-ink-3">Measure</span>
        <span className="text-2xs font-medium uppercase tracking-wider text-ink-3">Target</span>
        <span className="text-2xs font-medium uppercase tracking-wider text-ink-3">Weight</span>
        <span />
        {drafts.map((d, i) => {
          const usual = row.usual[d.measure];
          const unit = isMoney(d.measure) ? currency : d.measure === "Margin" ? "%" : "";
          return [
            <Select key={`m${i}`} aria-label="Measure" value={d.measure} onChange={(e) => change(i, { measure: e.target.value as TargetMeasure, value: "" })}>
              {measures.filter((m) => m === d.measure || !used.has(m)).map((m) => (
                <option key={m} value={m}>
                  {measureWord[m]}
                </option>
              ))}
            </Select>,
            <Input key={`v${i}`} aria-label={`${measureWord[d.measure]} target${unit ? ` in ${unit}` : ""}`} inputMode="decimal" value={d.value} onChange={(e) => change(i, { value: e.target.value })} placeholder={unit} aria-invalid={Boolean(errors[`targets[${i}].value`])} />,
            <Input key={`w${i}`} aria-label={`${measureWord[d.measure]} weight`} inputMode="numeric" value={d.weight} onChange={(e) => change(i, { weight: e.target.value })} aria-invalid={Boolean(errors[`targets[${i}].weight`])} />,
            <Button key={`r${i}`} type="button" variant="ghost" size="sm" aria-label={`Remove ${measureWord[d.measure]}`} onClick={() => setDrafts(drafts.filter((_, j) => j !== i))}>
              <X />
            </Button>,
            <p key={`u${i}`} className="col-span-4 -mt-1 text-xs text-ink-3">
              {errors[`targets[${i}].value`]?.[0] ?? errors[`targets[${i}].measure`]?.[0] ?? (usual !== undefined ? `Usual month: ${figure(d.measure, usual)}` : "No sales in the three months before.")}
            </p>,
          ];
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {drafts.length < 5 && (
          <Button type="button" size="sm" onClick={() => setDrafts([...drafts, { measure: measures.find((m) => !used.has(m))!, value: "", weight: "" }])}>
            <Plus />
            Add a measure
          </Button>
        )}
        <span className={weights === 100 ? "text-xs text-ink-3" : "text-xs text-warning"}>Weights {weights} of 100</span>
      </div>
      {(errors.targets || errors.month || (save.isError && !Object.keys(errors).length)) && (
        <p className="text-xs text-danger">{errors.targets?.[0] ?? errors.month?.[0] ?? save.error?.message}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || drafts.length === 0}>
          Save
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        {row.lines.length > 0 && (
          <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => submit([])}>
            Clear targets
          </Button>
        )}
      </div>
    </form>
  );
}
